// Copyright (c) 2018 Comsearch, Inc., All rights reserved

import React from 'react'
import { array, bool, func, object, oneOfType, string } from 'prop-types'
import { debounce } from 'lodash'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { warning } from 'react-notification-system-redux'
import { UNCAUGHT_ERROR } from 'errors'
import Async from 'react-select/async'
import { UpdatedMultiSelectAsyncWrapper } from './styles'
import UpdatedInputBase from '../UpdatedInputBase'

import {
  ErrorSimple,
  LabelSimple,
  LookupWrapper,
} from 'components/Form'

// NOTE - this does not extend from <UpdatedMultiSelect />.
// these two components build upon separate classes from 'react-select'

class UpdatedMultiSelectAsync extends UpdatedInputBase {
  static propTypes = {
    autoFocus: bool,
    className: string,
    clearable: bool,
    disabled: bool,
    id: string.isRequired,
    input: object.isRequired,
    isMulti: bool,
    label: oneOfType([
      string,
      object,
    ]),
    labelKey: string.isRequired,
    lookup: oneOfType([
      string,
      object,
    ]),
    meta: object.isRequired,
    noOptionsMessage: func,
    optionsResource: func.isRequired,
    resetValue: oneOfType([
      array,
      object,
    ]),
    removeSelected: bool,
    sourceForm: string.isRequired,
    valueKey: string.isRequired,
  }

  static defaultProps = {
    className: '',
    clearable: true,
    isMulti: true,
    removeSelected: true,
  }

  // we still want to use the inherited handleChange method, however
  // because of a special scenario with react-select/Async, we have to intervene beforehand.
  customOnChange = v => {
    // When using react-select/Async (since v3) and isMulti is true, there is inconsistent behavior
    // when clearing a field (returns an empty array) vs removing all items one at a time. (returns null)
    // https://github.com/JedWatson/react-select/issues/3585
    const value = (
      v === null && this.props.isMulti
        ? []
        : v
    )

    return this.handleChange(value)
  }

  // dynamically maps the option label to display
  getOptionLabel = option => option[this.props.labelKey]

  // dynamically maps the option value for component internals and for redux-form
  getOptionValue = option => option[this.props.valueKey]

  fetchData = async (value, callback) => {
    const {
      optionsResource,
      warning,
    } = this.props

    const options = []

    // if the input's empty, don't hit the API
    if (!value) {
      return callback(options)
    }

    try {
      const { entities } = await optionsResource({ value })

      // gets some results and boils them down to the bare minimum we need.
      // a label to display
      // and a value which we'll use later on for something else.

      // NOTE - this component does not need a filterOption like
      // MultiSelect because all options that come from the API will
      // be displayed and no filtering will be applied after that.
      options.push(...entities)
    } catch (err) {
      console.warn(err)
      // if there was an error in fetching from the resource, this will display a notification
      // in the top right to notify the user what's going on.
      warning({
        autoDismiss: 3,
        message: `MultiSelect Async Error - ${err.message || UNCAUGHT_ERROR}`,
      })
    }

    // callback passes the data to the <Async /> component so it can display the options.
    callback(options)
  }

  debouncedFetch = debounce(this.fetchData, 500)

  // all API requests are debounced by 500ms
  loadOptions = (inputValue, callback) => {
    this.debouncedFetch.cancel()
    this.debouncedFetch(inputValue, callback)
  }

  render () {
    const {
      autoFocus,
      className,
      clearable,
      disabled,
      input,
      id,
      isMulti,
      label,
      lookup,
      meta,
      noOptionsMessage,
      removeSelected,
    } = this.props

    return (
      <UpdatedMultiSelectAsyncWrapper className={`updated-multi-select-async ${className}`}>
        <LabelSimple
          idRoot={id}
          label={label} />
        <Async
          autoFocus={autoFocus}
          className={'input'}
          classNamePrefix={'react-select'}
          getOptionLabel={this.getOptionLabel}
          getOptionValue={this.getOptionValue}
          id={id}
          instanceId={id}
          isClearable={clearable}
          isDisabled={disabled}
          isMulti={isMulti}
          loadOptions={this.loadOptions}
          noOptionsMessage={noOptionsMessage}
          onChange={this.customOnChange}
          removeSelected={removeSelected}
          value={input.value} />
        <LookupWrapper
          lookup={lookup} />
        <ErrorSimple
          error={meta.error}
          idRoot={id} />
      </UpdatedMultiSelectAsyncWrapper>
    )
  }
}

// because of how redux-form and react-select are optimized,
// we basically need to trick react-select to trigger an update
// when a value is assigned to redux-form externally.
// (in a Lookup and from via the component for example)
// the easiest way to do this is to give it a single prop which
// looks at the length of redux-form's array. when this value changes,
// react-select will trigger an update and properly display the current value
const mapStateToProps = ({ form }, { input, sourceForm }) => ({
  // NOTE - null coalescing because if isMulti === false, this will
  // be an object or null whereas
  // if isMulti === true, then it will always be an array.
  numSelectedValues: form[sourceForm].values[input.name]?.length,
})

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    warning,
  }, dispatch)
)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UpdatedMultiSelectAsync)
